
import PrettyBar from "@/components/PrettyBar.vue";
import NewSidebar from "@/components/NewSidebar.vue";
import { defineComponent, ref, nextTick, watch } from "vue";
import { displayDanishTimestamp, downloadFile } from "@/composables/utils";
import type { BatchRoleAssignmentResult } from "./useBatchRoleAssignmentSidebar";
import { FilterMatchMode } from "primevue/api";
import VDButton from "@/components/VDButton.vue";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { isNullEmptyOrUndefined } from "@/utils/mist";

export default defineComponent({
  name: "BatchRoleAssignmentDetail",
  components: {
    NewSidebar,
    PrettyBar,
    VDButton,
  },
  props: {
    result: {
      type: Object as () => BatchRoleAssignmentResult,
      required: true,
    },
  },
  emits: ["hide"],
  setup(props, { emit }) {
    const sidebarRef = ref();

    const filters = ref({
      isSuccess: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    const isSuccess = ref(["true", "false"]);

    function handleHideSidebar() {
      sidebarRef.value?.hide();
      emit("hide");
    }

    watch(
      () => props.result,
      (newValue) => {
        if (newValue) {
          nextTick(() => {
            sidebarRef.value?.show();
          });
        }
      },
      { immediate: true }
    );

    const { client } = useApolloClient();
    const exportReportToExcelLoading = ref(false);

    const USER_ROLES_ASSIGNMENT_REPORT_EXCEL_QUERY = gql`
      query userRolesAssignmentReportExcel($isSimulation: Boolean) {
        userRolesAssignmentReportExcel(isSimulation: $isSimulation) {
          fileName
          contentType
          base64Data
        }
      }
    `;

    async function exportReportToExcel() {
      if (exportReportToExcelLoading.value) return;

      try {
        exportReportToExcelLoading.value = true;

        // Execute the query
        const result = await client.query({
          query: USER_ROLES_ASSIGNMENT_REPORT_EXCEL_QUERY,
          variables: { isSimulation: props.result.IsSimulation },
          fetchPolicy: "no-cache",
        });

        if (!result?.data?.userRolesAssignmentReportExcel) {
          console.warn("No data available for export");
          return;
        }

        const { fileName, contentType, base64Data } =
          result.data.userRolesAssignmentReportExcel;

        if (!base64Data || !fileName || !contentType) {
          console.warn("No file data received", {
            fileName,
            contentType,
            hasBase64: !!base64Data,
          });
          return;
        }

        downloadFile(fileName, contentType, base64Data);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        exportReportToExcelLoading.value = false;
      }
    }

    return {
      sidebarRef,
      handleHideSidebar,
      displayDanishTimestamp,
      filters,
      exportReportToExcelLoading,
      exportReportToExcel,
      isNullEmptyOrUndefined,
    };
  },
});
